import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import { LuX, LuMail, LuCircleCheck, LuCircleX, LuLoader, LuTriangleAlert } from "react-icons/lu";
import JsPDF from "jspdf";
import html2canvas from "html2canvas";
import FiniquitoChubbTemplate from "components/Siniestros/FiniquitoChubbTemplate";
import { validarCamposFiniquito } from "components/Siniestros/finiquitoValidation"; // ← NUEVO

const formatDateOnly = (value) => {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const formatEUR = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
};

async function generatePdfBase64(templateData, hiddenRef) {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      try {
        if (!hiddenRef.current) throw new Error("No se pudo renderizar la plantilla.");
        const canvas = await html2canvas(hiddenRef.current, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/jpeg");
        const pdf = new JsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
        pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
        const base64 = pdf.output("datauristring").split(",")[1];
        resolve(base64);
      } catch (err) {
        reject(err);
      }
    }, 100);
  });
}

const ROW_STATUS = { IDLE: "idle", GENERATING: "generating", SENDING: "sending", OK: "ok", ERROR: "error" };

export default function BulkSendFiniquitoModal({
  onClose,
  elegibles,
  apiBaseUrl,
  token,
  userId,
  onSuccess,
}) {
  const printRef = useRef(null);
  const [printData, setPrintData] = useState(null);
  const [rowStatus, setRowStatus] = useState(() =>
    Object.fromEntries(elegibles.map((s) => [s.id, { status: ROW_STATUS.IDLE, error: null }]))
  );
  const [globalError, setGlobalError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [franquiciaEmails, setFranquiciaEmails] = useState({});
  const [validacionesPrevias, setValidacionesPrevias] = useState({}); // ← NUEVO

  useEffect(() => {
    if (!elegibles.length) return;
    const fetchEmails = async () => {
      const entries = await Promise.all(
        elegibles.map(async (sin) => {
          if (!sin.delegacion_propietaria) return [sin.id, null];
          try {
            const res = await fetch(`${apiBaseUrl}/franquicias/${sin.delegacion_propietaria}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return [sin.id, null];
            const data = await res.json();
            return [sin.id, data?.email || null];
          } catch {
            return [sin.id, null];
          }
        })
      );
      setFranquiciaEmails(Object.fromEntries(entries));
    };
    fetchEmails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ← NUEVO: useEffect de validaciones, justo después del de emails
  useEffect(() => {
    if (!elegibles.length) return;
    const fetchValidaciones = async () => {
      const entries = await Promise.all(
        elegibles.map(async (sin) => {
          try {
            const sinRes = await fetch(`${apiBaseUrl}/siniestros/${sin.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const siniestro = sinRes.ok ? await sinRes.json() : sin;
            let franquicia = null;
            const codigoFranq =
              siniestro?.delegacion_propietaria ?? sin?.delegacion_propietaria;
            if (codigoFranq) {
              const frRes = await fetch(`${apiBaseUrl}/franquicias/${codigoFranq}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (frRes.ok) franquicia = await frRes.json();
            }
            return [sin.id, validarCamposFiniquito(siniestro, franquicia)];
          } catch {
            return [sin.id, []];
          }
        })
      );
      setValidacionesPrevias(Object.fromEntries(entries));
    };
    fetchValidaciones();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sentCount = Object.values(rowStatus).filter((r) => r.status === ROW_STATUS.OK).length;
  const errorCount = Object.values(rowStatus).filter((r) => r.status === ROW_STATUS.ERROR).length;
  const noElegibles = elegibles.length === 0;
  const canClose = !isSending;

  // ← NUEVO: variables calculadas para bloqueo
  const validacionLista = Object.keys(validacionesPrevias).length === elegibles.length;
  const hayElegiblesSinCampos = validacionLista &&
    Object.values(validacionesPrevias).some((errs) => errs.length > 0);

  const setStatus = (id, status, error = null) =>
    setRowStatus((prev) => ({ ...prev, [id]: { status, error } }));

  const handleSend = async () => {
    if (isSending) return;
    setIsSending(true);
    setGlobalError(null);

    for (let i = 0; i < elegibles.length; i++) {
      const sin = elegibles[i];

      let siniestro = sin;
      let franquicia = null;

      try {
        const sinRes = await fetch(`${apiBaseUrl}/siniestros/${sin.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (sinRes.ok) {
          siniestro = await sinRes.json();
        } else {
          console.warn("No se pudo obtener el detalle del siniestro, se usará el dato del listado", sin.id);
        }
      } catch (err) {
        console.warn("Error obteniendo detalle del siniestro, se usará el dato del listado", sin.id, err);
      }

      try {
        const codigoFranq = siniestro?.delegacion_propietaria ?? sin?.delegacion_propietaria;
        if (codigoFranq) {
          const frRes = await fetch(`${apiBaseUrl}/franquicias/${codigoFranq}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (frRes.ok) franquicia = await frRes.json();
          if (franquicia?.email) {
            setFranquiciaEmails((prev) => ({ ...prev, [sin.id]: franquicia.email }));
          }
        }
      } catch (_) {}

      console.log("DEBUG importe finiquito email", {
        id: sin.id,
        importeListado: sin?.importe_neto_cobrar,
        importeDetalle: siniestro?.importe_neto_cobrar,
        siniestro,
      });

      // ← NUEVO: validación antes de generar PDF
      const erroresCampos = validarCamposFiniquito(siniestro, franquicia);
      if (erroresCampos.length > 0) {
        setStatus(
          sin.id,
          ROW_STATUS.ERROR,
          `Campos incompletos: ${erroresCampos.map((e) => e.etiqueta).join(", ")}`
        );
        continue;
      }

      const templateData = {
        tipoPago: siniestro?.tipo_pago ?? "",
        fechaHoy: formatDateOnly(siniestro?.updated_at),
        refFast: siniestro?.ref_fast ?? "",
        refAlbaran: siniestro?.ref_albaran ?? "",
        aseguradoNombre: siniestro?.nombre_franquiciado ?? "",
        propiedadFranquicia:
          franquicia?.propiedad_franquicia ??
          siniestro?.nombre_franquiciado ??
          "",
        fechaSiniestro: formatDateOnly(siniestro?.fecha_siniestro),
        importeNetoCobrar: formatEUR(siniestro?.importe_neto_cobrar),
        cuenta: siniestro?.cuenta ?? "",
      };

      setStatus(sin.id, ROW_STATUS.GENERATING);
      setPrintData(templateData);

      let pdfBase64;
      try {
        pdfBase64 = await generatePdfBase64(templateData, printRef);
      } catch (err) {
        setStatus(sin.id, ROW_STATUS.ERROR, "Error generando PDF: " + (err?.message || ""));
        continue;
      }

      setStatus(sin.id, ROW_STATUS.SENDING);
      try {
        const res = await fetch(`${apiBaseUrl}/siniestros/${userId}/bulk/send-finiquito`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            ids: [sin.id],
            pdfs: [{
              id: sin.id,
              base64: pdfBase64,
              filename: `Finiquito_${siniestro?.ref_albaran || siniestro?.IDInterno || sin.id}.pdf`,
            }],
          }),
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok) throw new Error(payload?.error || "Error en el servidor");

        try {
          await fetch(`${apiBaseUrl}/siniestros/${userId}/bulk/estado`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ids: [sin.id], estado_siniestro: "ENVIO FINIQUITO MRW" }),
          });
        } catch (_) {}

        setStatus(sin.id, ROW_STATUS.OK);
      } catch (err) {
        setStatus(sin.id, ROW_STATUS.ERROR, err?.message || "Error enviando email");
      }

      if (i < elegibles.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    }

    setIsSending(false);
    setIsDone(true);
    setPrintData(null);
    onSuccess?.();
  };

  return (
    <>
      <div style={{ position: "fixed", left: "-99999px", top: 0, width: "794px", background: "#fff", zIndex: -1, pointerEvents: "none" }}>
        <div ref={printRef}>
          {printData ? <FiniquitoChubbTemplate data={printData} /> : null}
        </div>
      </div>

      <div
        className="fixed inset-0 z-30 overflow-y-auto bg-black/50 backdrop-blur-sm"
        aria-labelledby="send-finiquito-modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div className="relative inline-block w-full max-w-2xl p-7 overflow-hidden text-left align-middle transition-all transform bg-white border-2 shadow-xl outline-none sm:my-8 rounded-2xl border-[#FCAE52]">

            {/* Header */}
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-[3px] border-[#FCAE52]">
                  <LuMail className="text-gray-700" size={20} />
                </div>
                <div>
                  <p id="send-finiquito-modal-title" className="text-lg font-semibold text-gray-800">
                    Envío de finiquitos
                  </p>
                  <p className="text-sm text-gray-500">
                    {noElegibles
                      ? "Ningún registro elegible"
                      : `${elegibles.length} finiquito${elegibles.length !== 1 ? "s" : ""} para enviar`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={!canClose}
                className="flex items-center justify-center p-1.5 text-gray-400 transition-colors duration-200 bg-transparent rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Cerrar modal"
              >
                <LuX size={22} />
              </button>
            </div>

            {/* Caso sin elegibles */}
            {noElegibles ? (
              <div className="flex items-start gap-3 mb-5 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-lg">
                <LuTriangleAlert size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">
                    Sin registros elegibles para enviar
                  </p>
                  <p className="text-xs text-amber-700">
                    Los registros seleccionados no cumplen los requisitos necesarios. Solo se pueden enviar finiquitos de siniestros en estado <strong>ACEPTADO CHUBB</strong>.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Aviso info amarillo — solo antes de enviar */}
                {!isDone && !isSending && (
                  <div className="flex items-start gap-2 mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <LuTriangleAlert size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      Se generará y enviará el PDF del finiquito al{" "}
                      <span className="font-semibold">email configurado en cada franquicia</span>.
                      Este proceso puede tardar unos segundos por siniestro.
                    </p>
                  </div>
                )}

                {/* ← NUEVO: aviso rojo campos incompletos */}
                {hayElegiblesSinCampos && !isDone && !isSending && (
                  <div className="flex items-start gap-2 mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                    <LuTriangleAlert size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-red-700">
                      <p className="font-semibold mb-1">
                        Los siguientes siniestros tienen campos obligatorios incompletos y no se podrán enviar:
                      </p>
                      {elegibles
                        .filter((s) => validacionesPrevias[s.id]?.length > 0)
                        .map((s) => (
                          <div key={s.id} className="mb-1">
                            <span className="font-semibold">
                              {s.IDInterno || s.ref_albaran || `ID ${s.id}`}:
                            </span>
                            <ul className="ml-3 mt-0.5">
                              {validacionesPrevias[s.id].map((e) => (
                                <li key={e.campo}>• {e.etiqueta}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Tabla */}
                <div className="mb-4 overflow-hidden border border-gray-200 rounded-lg">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[18%]" />
                      <col className="w-[20%]" />
                      <col className="w-[36%]" />
                      <col className="w-[26%]" />
                    </colgroup>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">ID Interno</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ref. Albarán</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email destino</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                      </tr>
                    </thead>
                  </table>
                  <div className="overflow-y-auto" style={{ maxHeight: "400px" }}>
                    <table className="w-full text-sm table-fixed">
                      <colgroup>
                        <col className="w-[18%]" />
                        <col className="w-[20%]" />
                        <col className="w-[36%]" />
                        <col className="w-[26%]" />
                      </colgroup>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {elegibles.map((sin) => {
                          const rs = rowStatus[sin.id];
                          const emailDestino = franquiciaEmails[sin.id];
                          const emailLoaded = sin.id in franquiciaEmails;
                          const erroresFila = validacionesPrevias[sin.id] ?? []; // ← NUEVO
                          const filaValidada = sin.id in validacionesPrevias;    // ← NUEVO
                          return (
                            <tr key={sin.id}>
                              <td className="px-3 py-2 font-medium text-gray-800 truncate">
                                {sin.IDInterno || "—"}
                              </td>
                              <td className="px-3 py-2 text-gray-600 truncate">
                                {sin.ref_albaran || "—"}
                              </td>
                              <td className="px-3 py-2 text-xs">
                                {!emailLoaded ? (
                                  <span className="text-gray-400 italic">Cargando...</span>
                                ) : emailDestino ? (
                                  <span className="text-blue-600 font-medium break-all">{emailDestino}</span>
                                ) : (
                                  <span className="text-red-400 font-medium">Sin email configurado</span>
                                )}
                                {/* ← NUEVO: indicador de campos incompletos por fila */}
                                {filaValidada && erroresFila.length > 0 && (
                                  <span
                                    className="block text-red-400 font-semibold text-xs mt-0.5"
                                    title={erroresFila.map((e) => e.etiqueta).join(", ")}
                                  >
                                    ⚠ {erroresFila.length} campo{erroresFila.length !== 1 ? "s" : ""} incompleto{erroresFila.length !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <RowStatusBadge status={rs.status} error={rs.error} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Resumen final */}
                {isDone && (
                  <div className={`flex items-center gap-2 mb-4 px-3 py-2.5 rounded-lg border text-sm font-semibold ${
                    errorCount === 0
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "bg-amber-50 border-amber-200 text-amber-700"
                  }`}>
                    {errorCount === 0 ? <LuCircleCheck size={16} /> : <LuTriangleAlert size={16} />}
                    {sentCount} enviado{sentCount !== 1 ? "s" : ""} correctamente
                    {errorCount > 0 && ` · ${errorCount} con error`}
                  </div>
                )}

                {globalError && <p className="mb-3 text-sm text-red-600">{globalError}</p>}
              </>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={!canClose}
                className="h-10 px-5 text-sm font-semibold text-gray-600 transition-colors duration-150 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {isDone ? "Cerrar" : "Cancelar"}
              </button>
              {!noElegibles && !isDone && (
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending || hayElegiblesSinCampos} // ← NUEVO: añadido hayElegiblesSinCampos
                  className="h-10 px-5 text-sm font-semibold text-white transition-colors duration-150 bg-green-600 border border-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSending ? (
                    <><LuLoader size={15} className="animate-spin" /> Enviando...</>
                  ) : (
                    <><LuMail size={15} /> Confirmar envío</>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

function RowStatusBadge({ status, error }) {
  if (status === ROW_STATUS.IDLE)
    return <span className="text-xs text-gray-400">Pendiente</span>;
  if (status === ROW_STATUS.GENERATING)
    return <span className="flex items-center gap-1 text-xs text-blue-500"><LuLoader size={12} className="animate-spin" /> Generando PDF...</span>;
  if (status === ROW_STATUS.SENDING)
    return <span className="flex items-center gap-1 text-xs text-amber-500"><LuLoader size={12} className="animate-spin" /> Enviando...</span>;
  if (status === ROW_STATUS.OK)
    return <span className="flex items-center gap-1 text-xs text-green-600 font-semibold"><LuCircleCheck size={13} /> Enviado</span>;
  if (status === ROW_STATUS.ERROR)
    return <span className="flex items-center gap-1 text-xs text-red-500 font-semibold" title={error}><LuCircleX size={13} /> Error</span>;
  return null;
}

RowStatusBadge.propTypes = {
  status: PropTypes.string,
  error: PropTypes.string,
};

BulkSendFiniquitoModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  elegibles: PropTypes.arrayOf(PropTypes.object).isRequired,
  apiBaseUrl: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
};